import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import {
  CompensationRootState,
  setIsCompensationRecommendationBottomSheetOpen,
} from 'Deer/intent/Compensation/components/slice/compensationDrawersSlice';
import { ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import { useDispatch, useSelector } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { detailsColumnDefs, detailsRowData } from '../../json/PlanningGridData';
import {
  BaseRateCellRenderer,
  ProposedBaseSalaryCellRenderer,
  RecommendationCellRenderer,
  COMPA_RATIO_CELL_RENDERER,
  EffectiveDateCellRenderer,
  EmployeeCellRenderer,
  RatingCellRenderer,
  RATING_TEXT_AND_COLORS,
  WaitingOnCellRenderer,
  ChurnLikelihoodCellRenderer,
  CHURN_TEXT_AND_COLORS,
  WAITING_ON_STATUS_MAP,
} from '../cellRenderers/CellRenderers';

const DETAILS_GRID_COLUMN_CELL_RENDERERS = {
  EMPLOYEE: EmployeeCellRenderer,
  RATING: RatingCellRenderer,
  CHURN_LIKELIHOOD: ChurnLikelihoodCellRenderer,
  WAITING_ON: WaitingOnCellRenderer,
  EFFECTIVE_DATE: EffectiveDateCellRenderer,
  COMPA_RATIO: COMPA_RATIO_CELL_RENDERER,
  BASE_RATE: BaseRateCellRenderer,
  RECOMMENDATION: RecommendationCellRenderer,
  APPROVED_PAY: RecommendationCellRenderer,
  FINAL_PAY: ProposedBaseSalaryCellRenderer,
};

const DETAILS_GRID_COLUMN_ADDITIONAL_PROPS = {
  EMPLOYEE: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.EMPLOYEE?.displayName,
    },
  },
  RATING: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) =>
        RATING_TEXT_AND_COLORS[params?.data?.RATING]?.text || params?.data?.RATING,
    },
    width: 200,
  },
  CHURN_LIKELIHOOD: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) =>
        CHURN_TEXT_AND_COLORS[params?.data?.CHURN_LIKELIHOOD]?.text || params?.data?.CHURN_LIKELIHOOD,
    },
    width: 120,
  },
  WAITING_ON: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) =>
        WAITING_ON_STATUS_MAP[params?.data?.WAITING_ON?.status] || params?.data?.WAITING_ON?.status,
    },
  },
  EFFECTIVE_DATE: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.EFFECTIVE_DATE,
    },
    width: 150,
  },
  COMPA_RATIO: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.COMPA_RATIO,
    },
  },
  BASE_RATE: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params.data?.BASE_RATE?.value,
    },
    width: 120,
  },
  RECOMMENDATION: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params.data?.RECOMMENDATION?.value,
    },
    width: 150,
  },
  APPROVED_PAY: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params.data?.APPROVED_PAY?.value,
    },
    width: 120,
  },
  FINAL_PAY: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params.data?.FINAL_PAY?.value,
    },
    width: 300,
  },
  FLAGGED_PROPOSAL: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => (params?.data?.FLAGGED_PROPOSAL ? 'Yes' : 'No'),
    },
  },
};

export const PlanningGridDetails = (props) => {
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const isFlaggedProposalsFilterApplied = useSelector(
    (state: CompensationRootState) => state.compensationDrawers.isFlaggedProposalsFilterApplied
  );

  const applyFlaggedProposalFilter = useCallback(() => {
    if (!gridRef.current?.api) {
      return;
    }

    if (isFlaggedProposalsFilterApplied) {
      gridRef.current.api.setFilterModel({
        FLAGGED_PROPOSAL: {
          filterType: 'multi',
          filterModels: [
            null,
            {
              values: ['Yes'],
              filterType: 'set',
            },
            null,
          ],
        },
      });
    } else {
      gridRef.current.api.setFilterModel({
        FLAGGED_PROPOSAL: null,
      });
    }
  }, [isFlaggedProposalsFilterApplied, gridRef]);

  useEffect(() => {
    applyFlaggedProposalFilter();
  }, [isFlaggedProposalsFilterApplied, applyFlaggedProposalFilter]);

  const columnDefs = useMemo(
    () =>
      detailsColumnDefs
        .map((col) => ({
          colId: col.columnId,
          field: col.columnId,
          headerName: col.displayName,
          headerComponent: CustomHeaderCell,
          sortable: true,
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          autoHeaderHeight: true,
          wrapHeaderText: true,
          cellRenderer: DETAILS_GRID_COLUMN_CELL_RENDERERS[col.columnId] || ((params) => <Box>{params.value}</Box>),
          ...(DETAILS_GRID_COLUMN_ADDITIONAL_PROPS[col.columnId] || {}),
          suppressColumnsToolPanel: col.columnId === 'FLAGGED_PROPOSAL',
          hide: col.columnId === 'FLAGGED_PROPOSAL',
        }))
        .filter((col) => col.colId !== 'EMPLOYEE'),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      field: 'EMPLOYEE',
      pinned: 'left',
      headerName: 'Employee name',
      headerComponent: CustomHeaderCell,
      minWidth: 300,
      cellRendererParams: {
        innerRenderer: EmployeeCellRenderer,
        suppressCount: true,
      },
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: (params) => params.data?.hierarchy?.length === 1,
      ...(DETAILS_GRID_COLUMN_ADDITIONAL_PROPS.EMPLOYEE || {}),
    }),
    []
  );

  const handleRowGroupOpened = (params) => params.api.redrawRows({ rowNodes: [params.node] });

  const getRowStyle = (params) => {
    const { node } = params;
    if (node.expanded) return { backgroundColor: '#FAFAFA' };
    if (node.level > 0 && node.parent?.expanded) return { backgroundColor: '#FAFAFA' };
    return {};
  };

  const handleRowClicked = useCallback(
    (params: RowClickedEvent) => {
      const clickedElement = params.event.target as HTMLElement;
      if (clickedElement?.closest('[data-interactive="true"]')) return;
      dispatch(setIsCompensationRecommendationBottomSheetOpen(true));
    },
    [dispatch]
  );

  return (
    <Box pl="85px" pt="0" pr="16px" bg="#fafafa">
      <Box bg="#f5f7f9" borderRadius="8px" p="8px">
        <Box bg="white" borderRadius="8px" p="12px">
          <Box className="ag-details-grid compensation-planning-details-grid">
            <AgGridTable
              ref={gridRef}
              columnDefs={columnDefs}
              rowData={detailsRowData}
              defaultColDef={{
                minWidth: 120,
                resizable: true,
              }}
              autoGroupColumnDef={autoGroupColumnDef}
              rowHeight={88}
              pagination
              paginationPageSize={10}
              treeData
              animateRows
              groupDefaultExpanded={0}
              getDataPath={(data) => data.hierarchy}
              getRowStyle={getRowStyle}
              onRowGroupOpened={handleRowGroupOpened}
              rowSelection="multiple"
              suppressRowClickSelection
              showSidebarToggle={false}
              sideBar={null}
              onGridReady={applyFlaggedProposalFilter}
              onRowClicked={handleRowClicked}
              {...props}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
